import { startSiteActivity } from './utils/siteActivity';
import { createApp } from 'vue';
import { handwritingDirective } from './utils/siteHandwriting';
import { createRouter, createWebHashHistory } from 'vue-router';
import App from './App.vue';
import './assets/css/scroll-animations.css';
import './assets/css/mobile-interactions.css';
import './assets/css/ask-related.css';
import './assets/css/text-links.css';
import { initTextLinkHighlights } from './utils/textLinkHighlights';
import { initImageEnlargement } from './utils/imageEnlargement';
import './assets/css/image-enlargement.css';
import './assets/css/text-cursor.css';
import EducationTab from './components/EducationTab.vue';
import ExperienceTab from './components/ExperienceTab.vue';
import ResearchTab from './components/ResearchTab.vue';
import WritingInterviewPage from './components/WritingInterviewPage.vue';
import ProjectsTab from './components/ProjectsTab.vue';
import JourneyTab from './components/JourneyTab.vue';
import PlaybookTab from './components/PlaybookTab.vue';
import LexadayPage from './components/LexadayPage.vue';
import DealtrackerPage from './components/DealtrackerPage.vue';
import StrideSupportPage from './components/StrideSupportPage.vue';
import ScrollytellingPage from './components/ScrollytellingPage.vue';
import GeneratedViewPage from './components/GeneratedViewPage.vue';
import PublicViewsPage from './components/PublicViewsPage.vue';
import EarlyPage from './components/EarlyPage.vue';
import SearchStoryPage from './components/SearchStoryPage.vue';
import RagflowPipelinePage from './components/RagflowPipelinePage.vue';
import ItOperationsPage from './components/ItOperationsPage.vue';
import DocumentReviewPage from './components/DocumentReviewPage.vue';
import FragmentsTab from './components/FragmentsTab.vue';
import TrafficTab from './components/TrafficTab.vue';
import ResumeTab from './components/ResumeTab.vue';
import IndexPage from './components/IndexPage.vue';
import HomeConceptPage from './components/HomeConceptPage.vue';
import HomePage from './components/home/HomePage.vue';
import RoomExperience from './components/RoomExperience.vue';
import ArrowUpRightIcon from './components/ui/ArrowUpRightIcon.vue';
import SiteIcon from './components/ui/SiteIcon.vue';
import NotFoundPage from './components/NotFoundPage.vue';
import { hashEntryURL } from './utils/siteEntry';
import { startSiteAnalytics } from './utils/siteAnalytics';
import { cancelAnchorLanding, settleScrollToId } from './utils/anchorLanding';
import { clearRouteReturn, getRouteArrival, getRouteReturn, prepareRouteArrival, waitForRouteExit } from './utils/routeScroll';

if (typeof window !== 'undefined') {
  const entry = hashEntryURL(window.location);
  if (entry) window.history.replaceState(window.history.state, '', entry);
  window.__personalPageInitialHash = window.location.hash || '#/';
  window.__personalPageDirectHomeConsumed = false;
  // Vue Router owns restoration for every route. Leaving the browser's native
  // restoration enabled as well creates a second, delayed scroll on mobile
  // Chrome after the first frame has already rendered.
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
}

function isTouchFirstBrowser() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  return coarsePointer && navigator.maxTouchPoints > 0;
}

function isHomePath(path) {
  return path === '/' || path === '/home';
}

// Define your routes with meta titles
const routes = [
  {
    path: '/about',
    redirect: to => ({
      path: to.hash === '#showcaseimages' ? '/travels' : '/home',
      query: to.query,
      hash: ({
        '#how-i-research-and-build': '#story-research',
        '#outside-work': '#story-life',
        '#say-hello': '#story-contact',
      })[to.hash] || to.hash || '#story-about',
    }),
  },
  { path: '/education', component: EducationTab, meta: { title: 'Education - H.W.' } },
  { path: '/experience', component: ExperienceTab, meta: { title: 'Experience - H.W.' } },
  { path: '/research/ai-writing-interview', component: WritingInterviewPage, meta: { title: 'How AI changes the course of writing - H.W.' } },
  { path: '/research', component: ResearchTab, meta: { title: 'Research - H.W.' } },
  { path: '/projects', component: ProjectsTab, meta: { title: 'Builds - H.W.' } },
  {
    path: '/cathayflex',
    component: ProjectsTab,
    beforeEnter: to => {
      window.location.replace('/cathayflex/' + to.fullPath.slice('/cathayflex'.length).replace(/^\//, ''));
      return false;
    },
  },
  {
    path: '/slidepoise',
    component: ProjectsTab,
    meta: { title: 'SlidePoise - H.W.' },
    beforeEnter: (to) => {
      const suffix = to.fullPath.slice('/slidepoise'.length).replace(/^\//, '');
      window.location.assign('/slidepoise/' + suffix);
      return false;
    },
  },
  { path: '/travels', component: JourneyTab, meta: { title: 'Travels - H.W.' } },
  { path: '/playbook', component: PlaybookTab, meta: { title: 'Insurance Product Configuration Engine - H.W.' } },
  { path: '/lexaday', component: LexadayPage, meta: { title: 'Lexaday - H.W.' } },
  { path: '/stride', component: StrideSupportPage, meta: { title: 'Stride Support - H.W.' } },
  { path: '/dealtracker', component: DealtrackerPage, meta: { title: 'Dealtracker - H.W.' } },
  { path: '/scrollytelling', component: ScrollytellingPage, meta: { title: 'Scrollytelling Engine - H.W.' } },
  { path: '/view', component: GeneratedViewPage, meta: { title: 'Composed view - H.W.' } },
  { path: '/view/:shareId', component: GeneratedViewPage, meta: { title: 'Shared view - H.W.' } },
  { path: '/exhibition-showroom', component: () => import('./components/ExhibitionShowroomPage.vue'), meta: { title: 'The exhibition study room - H.W.' } },
  { path: '/views', component: PublicViewsPage, meta: { title: 'Views - H.W.' } },
  { path: '/early', component: EarlyPage, meta: { title: 'How I Started Building with Code - H.W.' } },
  { path: '/aipersonalarchive', component: SearchStoryPage, meta: { title: 'AI × Self - H.W.' } },
  {
    path: '/viewgallery',
    redirect: to => ({ path: '/aipersonalarchive', query: to.query, hash: '#case-gallery' }),
  },
  { path: '/ragflow-pipeline', component: RagflowPipelinePage, meta: { title: 'A Structure-Preserving RAG Ingestion Pipeline - H.W.' } },
  { path: '/document-review', component: DocumentReviewPage, meta: { title: 'Risk Assessment Across Long Documents - H.W.' } },
  { path: '/ai-it-operations', component: ItOperationsPage, meta: { title: 'AI for IT Operations - H.W.' } },
  { path: '/fragments', component: FragmentsTab, meta: { title: 'Fragments - H.W.' } },
  { path: '/traffic', component: TrafficTab, meta: { title: 'Traffic - H.W.' } },
  { path: '/cv', component: ResumeTab, meta: { title: 'CV - Han-yu Wang' } },
  { path: '/home', alias: '/', component: HomePage, meta: { monochromeHome: true, title: 'Henry Wang' } },
  { path: '/archive/home-2026-10-10', component: HomeConceptPage, meta: { noindex: true, title: 'Archived homepage - H.W.' } },
  { path: '/home-concept', redirect: to => ({ path: '/home', query: to.query, hash: to.hash }) },
  { path: '/archive/home', component: IndexPage, meta: { noindex: true, title: 'Archived homepage - H.W.' } },
  { path: '/room', component: RoomExperience, meta: { title: 'A temporary listening room - H.W.' } },
  { path: '/:pathMatch(.*)*', component: NotFoundPage, meta: { title: 'Page not found - H.W.', notFound: true } }
];

// Create the router instance
// Local render bench uses the original components for exhibit photographs.
if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
  routes.push({ path: '/_exhibition-workbench', component: () => import('./dev/ExhibitionWorkbench.vue') });
  routes.push({ path: '/_home-studies', redirect: to => ({ path: '/home', query: to.query, hash: to.hash }) });
}
if (process.env.NODE_ENV === 'development') {
  routes.push({ path: '/_gallery-select', component: () => import('./dev/GallerySelection.vue'), meta: { noindex: true, title: 'Choose homepage photographs' } });
}
const router = createRouter({
  history: createWebHashHistory(),
  routes,
  async scrollBehavior(to, from, savedPosition) {
    const originReturn = getRouteReturn(to.fullPath);
    if (originReturn) {
      await originReturn.ready;
      clearRouteReturn(originReturn);
      if (router.currentRoute.value.fullPath !== to.fullPath) return false;
      return { top: originReturn.top, behavior: 'instant' };
    }
    // Keep the departing homepage at its reading position throughout its fade.
    // Resetting earlier briefly exposes chapter 01 while the old DOM is visible.
    if (isHomePath(from.path) && !isHomePath(to.path)) {
      await waitForRouteExit(document.querySelector('.home-concept-page, .mono-home'));
      if (router.currentRoute.value.fullPath !== to.fullPath) return false;
    }
    // The outgoing page can be shorter than the saved reading position.
    // Restore only after the destination has mounted and laid out, otherwise
    // the browser clamps the requested offset to the old page's height.
    const arrival = getRouteArrival(to.fullPath);
    if (arrival) {
      await arrival.ready;
      if (router.currentRoute.value.fullPath !== to.fullPath) return false;
    }
    // A fresh mobile load starts at the first composition. In-app history and
    // explicit chapter links retain their own destination and reading offset.
    if (isTouchFirstBrowser() && isHomePath(to.path) && !from.matched?.length && !to.hash) {
      return { top: 0 };
    }
    // If user clicks browser back/forward, restore scroll
    if (savedPosition) {
      return savedPosition;
    }
    // Travels hashes focus a globe point. A normal compact-screen arrival
    // lands the title just below the persistent header with a little air.
    if (to.path === '/travels') {
      if (!to.hash && window.matchMedia('(max-width: 1100px)').matches) {
        return new Promise((resolve) => {
          window.setTimeout(() => {
            const header = document.querySelector('.header');
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            resolve({ el: '#travels-title', top: Math.ceil(headerHeight) + 22 });
          }, 120);
        });
      }
      return { top: 0 };
    }
    // Keep the established cross-page glide after the destination is mounted.
    // Same-page controls continue to own their own scroll motion.
    if ((to.hash || (to.query && to.query.to)) && from.path !== to.path) {
      scheduleRouteLanding(to);
      return { top: 0 };
    }
    // A query-only change on the same page (e.g. stripping the ?to intent after
    // it's been consumed): leave the scroll position alone.
    if (from.path === to.path) {
      if ((to.hash && from.hash !== to.hash) || (to.query?.to && from.query?.to !== to.query.to)) scheduleRouteLanding(to);
      return false;
    }
    // Otherwise scroll to top for new routes
    return { top: 0 };
  }
});

// Each navigation owns its landing. Schedule only after scrollBehavior has
// resolved exit/return handling, so an earlier browser-back cannot suppress it.
let landingToken = 0;
router.afterEach((to, from, failure) => {
  if (failure) return;
  landingToken += 1;
  cancelAnchorLanding();
  prepareRouteArrival(from.matched?.length !== 0 && from.path !== to.path ? to.fullPath : null);
});

function scheduleRouteLanding(to) {
  if (to.path === '/travels' || to.path === '/fragments') return;
  const raw = (to.hash || '').replace(/^#/, '') || to.query?.to || '';
  let id;
  try { id = decodeURIComponent(raw); } catch { id = raw; }
  if (!id) return;
  const token = landingToken;
  let tries = 0;
  const land = () => {
    if (token !== landingToken || router.currentRoute.value.fullPath !== to.fullPath) return;
    if (settleScrollToId(id)) return;
    if (tries++ < 25) setTimeout(land, 100);
  };
  setTimeout(land, 30);
}

// Create the Vue app instance
const app = createApp(App);
app.directive('handwriting', handwritingDirective);
app.component('ArrowUpRightIcon', ArrowUpRightIcon);
app.component('SiteIcon', SiteIcon);

router.afterEach(to => {
  document.title = to.meta.title || 'H.W. | Explore. Understand. Build.';
  let robots = document.querySelector('meta[data-route-robots]');
  if (to.meta.notFound || to.meta.noindex) {
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      robots.setAttribute('data-route-robots', '');
      document.head.appendChild(robots);
    }
    robots.content = 'noindex';
  } else robots?.remove();
});
startSiteAnalytics(router);

// Use the router
startSiteActivity(router);
app.use(router);

// Mount the app
app.mount('#app');
const stopTextLinkHighlights = initTextLinkHighlights();
const stopImageEnlargement = initImageEnlargement();
if (module.hot) module.hot.dispose(() => {
  stopTextLinkHighlights();
  stopImageEnlargement();
});
